import React, { useEffect, useState } from 'react';
import { intervalUpdatingData } from '../../utils/dynamic';
import { fetchData } from '../../utils/api';
import { apiUrl } from '../../config';
import UserView from '../../components/system/users/UserView';
import UserEdit from '../../components/system/users/UserEdit';
import UserNew from '../../components/system/users/UserNew';
import styles from '../../styles/scss/system/Users.module.scss';
import { useAdminLanguage } from '../../AdminLanguageContext';
import { translateAdmin } from '../../utils/adminTranslations';

const Users = () => {
  const { language } = useAdminLanguage();
  const translate = (text) => translateAdmin(text, language);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [currentUsername, setCurrentUsername] = useState('');
  const [usersMessage, setUsersMessage] = useState('Загрузка пользователей...');
  const [loading, setLoading] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isNewOpen, setIsNewOpen] = useState(false);
  const [editData, setEditData] = useState([]);
  const [viewData, setViewData] = useState([]);
  const [showPasswords, setShowPasswords] = useState({});
  // Изменяем структуру: храним пароли для каждого пользователя отдельно
  const [handbookingPasses, setHandbookingPasses] = useState({});
  const [openHandBookingPassUser, setOpenHandBookingPassUser] = useState(0);

  const handleVEDNClick = (user, action) => {
    switch (action) {
      case 'view':
        setViewData(user);
        setIsViewOpen(true);
        break;
      case 'edit':
        setEditData(user);
        setIsEditOpen(true);
        break;
      case 'new':
        setIsNewOpen(true);
        break;
      default:
        break;
    }
  };

  const handleVEClose = () => {
    setIsViewOpen(false);
    setIsEditOpen(false);
    setIsNewOpen(false);
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const data = await fetchData(`${apiUrl}/api/sys/get-users`, "GET", token);
      if (data.request.status === 0) {
        setUsers(
          data.request.data.users.map((user) => {
            const role = data.request.data.roles.find((role) => role.id === user.role_id);
            return {
              ...user,
              role_name: role ? role.role_name : 'Неизвестно',
            };
          })
        );
        setRoles(data.request.data.roles);
        setCurrentUsername(data.request.data.current_username);
        setUsersMessage('');
      } else {
        setUsersMessage(data.request.message);
      }
    } catch (error) {
      console.error("Ошибка:", error);
      setUsersMessage('Не удалось загрузить пользователей.');
    } finally {
      setLoading(false);
    }
  };

  const deluser = async (id) => {
    const target = users.find((user) => user.id === id);
    if (target && target.username === currentUsername) {
      alert(translate('Нельзя удалить свою учетную запись.'));
      return;
    }
    if (target && target.role_name === 'admin') {
      alert(translate('Нельзя удалить пользователя с ролью admin.'));
      return;
    }
    if (target && target.role_name === localStorage.getItem('asd')) {
      alert(translate('Нельзя изменять пользователя с такой же ролью.'));
      return;
    }

    const confirm = window.confirm(translate("Вы уверены, что хотите удалить этого пользователя?"));
    if (!confirm) return;
    const token = localStorage.getItem('accessToken');
    const data = await fetchData(`${apiUrl}/api/sys/rmdel-users/${id}`, "GET", token);
    if (data.request.status === 0) {
      setUsers(prev => prev.filter(user => user.id !== data.request.id));
    } else {
      alert(translate(data.request.message));
    }
  };

  useEffect(() => {
    fetchUsers();
    const interval = setInterval(() => {
      setLoading(true);
      setTimeout(() => {
        fetchUsers();
      }, 3000);
    }, intervalUpdatingData);
    return () => clearInterval(interval);
  }, []);

  const handleHandbookingPassChange = (userId, value) => {
    setHandbookingPasses(prev => ({
      ...prev,
      [userId]: value
    }));
  };

  const handleSaveHandbookingPass = async (userId) => {
    const token = localStorage.getItem('accessToken');
    const passValue = handbookingPasses[userId] || '';

    const data = await fetchData(
      `${apiUrl}/api/sys/handbookingpassset/${userId}`,
      "POST",
      token,
      { body: { "handbooking_pass": passValue } }
    );

    if (data.request.status === 0) {
      // Исправляем: используем user.id вместо user.userId
      setUsers(prevUsers => prevUsers.map(user =>
        user.id === userId
          ? { ...user, handbooking_pass: passValue }
          : user
      ));

      // Очищаем пароль для этого пользователя
      setHandbookingPasses(prev => {
        const newPasses = { ...prev };
        delete newPasses[userId];
        return newPasses;
      });

      // Закрываем режим редактирования
      setOpenHandBookingPassUser(0);
    } else {
      alert(translate(data.request.message));
    }
  };

  // Новая функция для сохранения пароля для всех не-админов
  const handleSaveHandbookingPassForAll = async (userId) => {
    const token = localStorage.getItem('accessToken');
    const passValue = handbookingPasses[userId] || '';

    if (!passValue) {
      alert(translate('Пароль не может быть пустым'));
      return;
    }

    const confirmSave = window.confirm(translate(`Вы уверены, что хотите установить этот пароль для ВСЕХ пользователей?`));
    if (!confirmSave) return;

    try {
      // Получаем всех не-админов
      const nonAdminUsers = users.filter(user => user.role_name !== 'admin');

      // Отправляем запросы для каждого пользователя
      const promises = nonAdminUsers.map(async (user) => {
        const data = await fetchData(
          `${apiUrl}/api/sys/handbookingpassset/${user.id}`,
          "POST",
          token,
          { body: { "handbooking_pass": passValue } }
        );
        return { userId: user.id, success: data.request.status === 0 };
      });

      const results = await Promise.all(promises);
      const successfulUpdates = results.filter(r => r.success);

      // Обновляем состояние для всех успешно обновленных пользователей
      setUsers(prevUsers => prevUsers.map(user => {
        if (user.role_name !== 'admin' && results.find(r => r.userId === user.id && r.success)) {
          return { ...user, handbooking_pass: passValue };
        }
        return user;
      }));

      alert(`${translate('Пароль успешно обновлен для')} ${successfulUpdates.length} ${translate('пользователей')}`);

      // Очищаем состояние
      setHandbookingPasses({});
      setOpenHandBookingPassUser(0);

    } catch (error) {
      console.error('Ошибка при обновлении паролей:', error);
      alert(translate('Произошла ошибка при обновлении паролей'));
    }
  };

  const handleOpenHandbookingPass = (userId) => {
    setOpenHandBookingPassUser(userId);
    // Устанавливаем текущий пароль пользователя как начальное значение
    const user = users.find(u => u.id === userId);
    if (user && (user.username === currentUsername || user.role_name !== localStorage.getItem('asd'))) {
      setHandbookingPasses(prev => ({
        ...prev,
        [userId]: user.handbooking_pass || ''
      }));
    }
  };

  const handleCancelEdit = () => {
    setHandbookingPasses({});
    setOpenHandBookingPassUser(0);
  };
  const togglePasswordVisibility = (userId) => {
    setShowPasswords(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  return (
    <>
      <section className={styles.usersPage}>
        <header className={styles.pageHeader}>
          <div>
            <span className={styles.eyebrow}>{translate('УПРАВЛЕНИЕ ДОСТУПОМ')}</span>
            <h2>{translate('Пользователи')}</h2>
            <p>{translate('Учетные записи сотрудников и их параметры доступа')}</p>
          </div>
          <div className={styles.headerActions}>
            {loading && <span className={styles.loadingLabel}>{translate('Обновление списка...')}</span>}
            <button
              type="button"
              className={styles.createButton}
              onClick={() => handleVEDNClick(null, 'new')}
            >
              <span aria-hidden="true">+</span>
              {translate('Добавить пользователя')}
            </button>
          </div>
        </header>

        <div className={styles.tableCard}>
          <div className={styles.tableHeading}>
            <div>
              <h3>{translate('Список пользователей')}</h3>
              <span>{users.length} {translate(users.length === 1 ? 'пользователь' : 'пользователей')}</span>
            </div>
          </div>
          <div className={styles.tableScroll} aria-busy={loading}>
            <table className={styles.usersTable}>
              <thead>
                <tr>
                  <th scope="col">№</th>
                  <th scope="col">{translate('Сотрудник')}</th>
                  <th scope="col">{translate('Логин')}</th>
                  <th scope="col">{translate('Роль')}</th>
                  <th scope="col">{translate('Пароль ручных броней')}</th>
                  <th scope="col">{translate('Действия')}</th>
                </tr>
              </thead>
              <tbody>
                {users.length > 0 ? users.map((user, index) => (
                  <tr key={user.id || index}>
                    <td data-label="№">{index + 1}</td>
                    <td data-label={translate('Сотрудник')}>
                      <span className={styles.employeeName}>
                        {[user.first_name, user.last_name].filter(Boolean).join(' ') || translate('Без имени')}
                      </span>
                    </td>
                    <td data-label={translate('Логин')}><span className={styles.login}>{user.username}</span></td>
                    <td data-label={translate('Роль')}><span className={styles.roleBadge}>{translate(user.role_name)}</span></td>
                    <td data-label={translate('Пароль ручных броней')}>
                      {user.role_name !== 'admin' &&
                        (user.username === currentUsername || user.role_name !== localStorage.getItem('asd')) && (
                        openHandBookingPassUser === user.id ? (
                          <div className={styles.passwordEditor}>
                            <input
                              aria-label={`${translate('Пароль ручных броней')}: ${user.username}`}
                              type={showPasswords[user.id] ? 'text' : 'password'}
                              value={handbookingPasses[user.id] ?? user.handbooking_pass ?? ''}
                              onChange={(e) => handleHandbookingPassChange(user.id, e.target.value)}
                            />
                            <button
                              type="button"
                              className={styles.iconButton}
                              onClick={() => togglePasswordVisibility(user.id)}
                              aria-label={translate(showPasswords[user.id] ? 'Скрыть пароль' : 'Показать пароль')}
                              title={translate(showPasswords[user.id] ? 'Скрыть пароль' : 'Показать пароль')}
                            >
                              {translate(showPasswords[user.id] ? 'Скрыть' : 'Показать')}
                            </button>
                            <button
                              type="button"
                              className={styles.smallButton}
                              onClick={() => handleSaveHandbookingPass(user.id)}
                            >
                              {translate('Сохранить')}
                            </button>
                            <button
                              type="button"
                              className={styles.secondaryButton}
                              onClick={() => handleSaveHandbookingPassForAll(user.id)}
                            >
                              {translate('Для всех')}
                            </button>
                            <button
                              type="button"
                              className={styles.cancelButton}
                              onClick={handleCancelEdit}
                              aria-label={translate('Отменить редактирование')}
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className={styles.secondaryButton}
                            onClick={() => handleOpenHandbookingPass(user.id)}
                          >
                            {translate('Изменить пароль')}
                          </button>
                        )
                      )}
                    </td>
                    <td data-label={translate('Действия')}>
                      <div className={styles.rowActions}>
                        <button
                          type="button"
                          className={styles.secondaryButton}
                          onClick={() => handleVEDNClick(user, 'view')}
                        >
                          {translate('Просмотр')}
                        </button>
                        <button
                          type="button"
                          className={styles.editButton}
                          disabled={user.username !== currentUsername && user.role_name === localStorage.getItem('asd')}
                          onClick={() => handleVEDNClick(user, 'edit')}
                        >
                          {translate('Изменить')}
                        </button>
                        <button
                          type="button"
                          className={styles.deleteButton}
                          disabled={user.username === currentUsername || user.role_name === 'admin' || user.role_name === localStorage.getItem('asd')}
                          onClick={() => deluser(user.id)}
                        >
                          {translate('Удалить')}
                        </button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td className={styles.emptyState} colSpan="6">
                      {translate(usersMessage || 'Пользователи не найдены.')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
      {isViewOpen && (
        <UserView user={viewData} onClose={handleVEClose} />
      )}
      {isEditOpen && (
        <UserEdit
          user={editData}
          rolesData={roles}
          onClose={handleVEClose}
          onSaved={fetchUsers}
          canChangeRole={editData.username !== currentUsername}
        />
      )}
      {isNewOpen && (
        <UserNew rolesData={roles} onClose={handleVEClose} onSaved={fetchUsers} />
      )}
    </>
  );
};

export default Users;